import { useState, useEffect } from "react";
import SearchForm from "../components/SearchForm";
import ListingCard from "../components/ListingCard";

const API_URL = process.env.REACT_APP_API_URL || process.env.VITE_API_URL || "http://localhost:8080";

export default function Listings() {
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [searchProfile, setSearchProfile] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [filters, setFilters] = useState({
    type: "ALL", // ALL, ALUGUEL, VENDA
    minPrice: "",
    maxPrice: "",
    minBedrooms: "",
    minBathrooms: "",
    minParking: "",
    portal: "ALL", // ALL, Quinto Andar, Viva Real, Zap Imóveis
  });

  // Carregar todos os anúncios ao montar o componente
  useEffect(() => {
    loadAllListings();
  }, []);

  // Aplicar filtros quando listings ou filters mudarem
  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listings, filters]);

  const loadAllListings = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/listings`, {
        headers: {
          "X-MCP-Protocol": "1.0",
        },
      });
      if (response.ok) {
        const data = await response.json();
        setListings(data.listings || []);
      }
    } catch (error) {
      console.error("Erro ao carregar anúncios:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIntelligentSearch = async () => {
    if (!searchProfile.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/listings/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-MCP-Protocol": "1.0",
        },
        body: JSON.stringify({ profile: searchProfile }),
      });
      if (response.ok) {
        const data = await response.json();
        setListings(data.listings || []);
      }
    } catch (error) {
      console.error("Erro na busca inteligente:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...listings];

    // Filtro por tipo
    if (filters.type !== "ALL") {
      filtered = filtered.filter((listing) => listing.type === filters.type);
    }

    // Filtro por preço mínimo
    if (filters.minPrice) {
      const minPrice = parseInt(filters.minPrice);
      filtered = filtered.filter((listing) => listing.price >= minPrice);
    }

    // Filtro por preço máximo
    if (filters.maxPrice) {
      const maxPrice = parseInt(filters.maxPrice);
      filtered = filtered.filter((listing) => listing.price <= maxPrice);
    }

    // Filtro por quartos
    if (filters.minBedrooms) {
      const minBedrooms = parseInt(filters.minBedrooms);
      filtered = filtered.filter(
        (listing) => listing.bedrooms && listing.bedrooms >= minBedrooms
      );
    }

    // Filtro por banheiros
    if (filters.minBathrooms) {
      const minBathrooms = parseInt(filters.minBathrooms);
      filtered = filtered.filter(
        (listing) => listing.bathrooms && listing.bathrooms >= minBathrooms
      );
    }

    // Filtro por vagas
    if (filters.minParking) {
      const minParking = parseInt(filters.minParking);
      filtered = filtered.filter(
        (listing) => listing.parkingSpots && listing.parkingSpots >= minParking
      );
    }

    // Filtro por portal
    if (filters.portal !== "ALL") {
      filtered = filtered.filter((listing) => listing.portal === filters.portal);
    }

    setFilteredListings(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
    });
  };

  const clearFilters = () => {
    setFilters({
      type: "ALL",
      minPrice: "",
      maxPrice: "",
      minBedrooms: "",
      minBathrooms: "",
      minParking: "",
      portal: "ALL",
    });
    setSearchProfile("");
    loadAllListings();
  };

  return (
    <div className="container">
      <section style={{ marginBottom: "2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
          <div>
            <h1 className="section-title" style={{ marginBottom: "0.5rem" }}>Busca Inteligente de Imóveis</h1>
            <p style={{ color: "var(--text-secondary)", textAlign: "left" }}>
              Descreva o imóvel dos seus sonhos e nossa IA encontrará as melhores opções para você
            </p>
          </div>
          <button
            className="btn-secondary"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{ 
              fontSize: "0.9rem", 
              padding: "0.75rem 1.25rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem"
            }}
          >
            {sidebarOpen ? "◀ Ocultar Filtros" : "▶ Mostrar Filtros"}
          </button>
        </div>

        <div className="panel" style={{ marginBottom: "2rem" }}>
          <SearchForm
            profile={searchProfile}
            onProfileChange={setSearchProfile}
            onSearch={handleIntelligentSearch}
            isLoading={isSearching}
          />
        </div>
      </section>

      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          style={{
            display: "none",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 40,
          }}
        />
      )}
      <div style={{ display: "flex", gap: "2rem", alignItems: "flex-start", position: "relative" }}>
        {/* Sidebar de Filtros */}
        <aside 
          className="filters-sidebar"
          style={{
            width: sidebarOpen ? "320px" : "0",
            minWidth: sidebarOpen ? "320px" : "0",
            overflow: "hidden",
            transition: "all 0.3s ease",
            flexShrink: 0
          }}
        >
          <div className="panel" style={{ 
            position: sidebarOpen ? "relative" : "absolute",
            width: "320px",
            padding: sidebarOpen ? "1.5rem" : "0",
            opacity: sidebarOpen ? 1 : 0,
            transition: "opacity 0.3s ease"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
              <h2 style={{ margin: 0, fontSize: "1.25rem" }}>Filtros</h2>
              <button
                className="btn-secondary"
                onClick={clearFilters}
                style={{ fontSize: "0.85rem", padding: "0.5rem 0.75rem" }}
              >
                Limpar
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div>
                <label htmlFor="filter-type" style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500, fontSize: "0.9rem" }}>
                  Tipo
                </label>
                <select
                  id="filter-type"
                  value={filters.type}
                  onChange={(e) => handleFilterChange("type", e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "0.9rem",
                  }}
                >
                  <option value="ALL">Todos</option>
                  <option value="ALUGUEL">Aluguel</option>
                  <option value="VENDA">Venda</option>
                </select>
              </div>

              <div>
                <label htmlFor="filter-portal" style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500, fontSize: "0.9rem" }}>
                  Portal
                </label>
                <select
                  id="filter-portal"
                  value={filters.portal}
                  onChange={(e) => handleFilterChange("portal", e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "0.9rem",
                  }}
                >
                  <option value="ALL">Todos</option>
                  <option value="Quinto Andar">Quinto Andar</option>
                  <option value="Viva Real">Viva Real</option>
                  <option value="Zap Imóveis">Zap Imóveis</option>
                </select>
              </div>

              <div>
                <label htmlFor="filter-min-price" style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500, fontSize: "0.9rem" }}>
                  Preço Mínimo (R$)
                </label>
                <input
                  id="filter-min-price"
                  type="number"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange("minPrice", e.target.value)}
                  placeholder="Ex: 1000"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "0.9rem",
                  }}
                />
              </div>

              <div>
                <label htmlFor="filter-max-price" style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500, fontSize: "0.9rem" }}>
                  Preço Máximo (R$)
                </label>
                <input
                  id="filter-max-price"
                  type="number"
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange("maxPrice", e.target.value)}
                  placeholder="Ex: 5000"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "0.9rem",
                  }}
                />
              </div>

              <div>
                <label htmlFor="filter-bedrooms" style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500, fontSize: "0.9rem" }}>
                  Mín. Quartos
                </label>
                <input
                  id="filter-bedrooms"
                  type="number"
                  min="0"
                  value={filters.minBedrooms}
                  onChange={(e) => handleFilterChange("minBedrooms", e.target.value)}
                  placeholder="Ex: 2"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "0.9rem",
                  }}
                />
              </div>

              <div>
                <label htmlFor="filter-bathrooms" style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500, fontSize: "0.9rem" }}>
                  Mín. Banheiros
                </label>
                <input
                  id="filter-bathrooms"
                  type="number"
                  min="0"
                  value={filters.minBathrooms}
                  onChange={(e) => handleFilterChange("minBathrooms", e.target.value)}
                  placeholder="Ex: 2"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "0.9rem",
                  }}
                />
              </div>

              <div>
                <label htmlFor="filter-parking" style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500, fontSize: "0.9rem" }}>
                  Mín. Vagas
                </label>
                <input
                  id="filter-parking"
                  type="number"
                  min="0"
                  value={filters.minParking}
                  onChange={(e) => handleFilterChange("minParking", e.target.value)}
                  placeholder="Ex: 1"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "0.9rem",
                  }}
                />
              </div>
            </div>
          </div>
        </aside>

        {/* Conteúdo Principal */}
        <main style={{ flex: 1, minWidth: 0 }}>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem", flexWrap: "wrap", gap: "1rem" }}>
            <h2 className="section-title" style={{ margin: 0 }}>
              {isLoading ? "Carregando..." : `${filteredListings.length} Anúncio${filteredListings.length !== 1 ? "s" : ""} Encontrado${filteredListings.length !== 1 ? "s" : ""}`}
            </h2>
            {!isLoading && (
              <button
                className="btn-secondary"
                onClick={loadAllListings}
                style={{ fontSize: "0.9rem", padding: "0.5rem 1rem" }}
              >
                Atualizar Lista
              </button>
            )}
          </div>

        {isLoading ? (
          <div style={{ textAlign: "center", padding: "4rem 0" }}>
            <p style={{ color: "var(--text-secondary)", fontSize: "1.1rem" }}>Carregando anúncios...</p>
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="panel" style={{ textAlign: "center", padding: "4rem 2rem" }}>
            <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>🏠</div>
            <h3 style={{ marginBottom: "0.5rem" }}>Nenhum anúncio encontrado</h3>
            <p style={{ color: "var(--text-secondary)", marginBottom: "1.5rem" }}>
              Tente ajustar os filtros ou fazer uma nova busca
            </p>
            <button className="btn-primary" onClick={clearFilters}>
              Limpar Filtros e Ver Todos
            </button>
          </div>
        ) : (
          <div className="grid grid-3">
            {filteredListings.map((listing, index) => (
              <ListingCard key={listing.id || index} listing={listing} />
            ))}
          </div>
        )}
        </main>
      </div>
    </div>
  );
}
